// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import Papa from 'papaparse';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '../../lib/base';
import { Help } from '../../lib/help';
import { CROP_NAMES_QUERY } from '../../queries/CropNamesQuery';
import { CROPS_QUERY } from '../../queries/CropsQueries';
import type {
  CreateCropsMutation,
  CreateCropsMutationVariables,
  GetExistingCropsForUploadQuery,
  GetExistingCropsForUploadQueryVariables,
} from '../../types/__generated__/graphql';
import { ExitDialogButton } from '../common/button';
import WarningBox from '../common/info/WarningBox';
import FileUpload from './FileUpload';
import ParseCropsTable from './ParseCropsTable';
import ParseErrorsTable from './ParseErrorsTable';
import {
  type CropImportRowError,
  type CropImportRowOut,
  downloadCropImportTemplate,
  validateCropImportRows,
  validateHeaders,
} from './parseCropUpload';

const CREATE_CROPS_MUTATION = gql`
  mutation CreateCrops($crops: [CreateSingularCropInput!]!, $should_edit_existing: Boolean!) {
    createCrops(input: { crops_to_create: $crops, should_edit_existing: $should_edit_existing }) {
      new_crops {
        id
      }
      new_crop_steps {
        id
      }
      crop_updated_count
    }
  }
`;

const GET_EXISTING_CROPS_QUERY = gql`
  query GetExistingCropsForUpload {
    crops {
      edges {
        node {
          id
          crop_name
        }
      }
    }
  }
`;

interface Props {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export default function UploadCropsDialog({ isOpen, onClose, onSuccess }: Props) {
  const [createCrops, { loading }] = useMutation<CreateCropsMutation, CreateCropsMutationVariables>(
    CREATE_CROPS_MUTATION,
  );
  const [getExistingCrops, { loading: loadingExistingCrops, data }] = useLazyQuery<
    GetExistingCropsForUploadQuery,
    GetExistingCropsForUploadQueryVariables
  >(GET_EXISTING_CROPS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const { preferredWeightUnit } = useUserContext();
  const [headerErrors, setHeaderErrors] = React.useState<readonly string[]>([]);
  const [rowErrors, setRowErrors] = React.useState<readonly CropImportRowError[]>([]);
  const [uploadedValues, setUploadedValues] = React.useState<readonly CropImportRowOut[]>([]);
  const [fileSelected, setFileSelected] = React.useState<File | null>(null);
  const [shouldEditExisting, setShouldEditExisting] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      void getExistingCrops();
    }
  }, [isOpen, getExistingCrops]);

  const existingCropNamesSet = React.useMemo(() => {
    return new Set<string>(data?.crops.edges.map(({ node: crop }) => crop.crop_name.trim().toLowerCase()) ?? []);
  }, [data]);

  const daysToSeconds = (days: number) => days * 24 * 60 * 60;
  const onSubmit = () => {
    void createCrops({
      refetchQueries: [{ query: CROPS_QUERY }, { query: CROP_NAMES_QUERY }],
      variables: {
        should_edit_existing: shouldEditExisting,
        crops: uploadedValues.map((c) => ({
          crop_name: c.name,
          total_grow_time: daysToSeconds(c.growTimeDays),
          yield_per_tray: c.yieldPerTray,
          yield_unit: c.yieldPerTrayUnit,
          max_hold_time: daysToSeconds(c.maxHoldTimeDays),
          harvest_buffer_time: c.harvestBufferDays ? daysToSeconds(c.harvestBufferDays) : undefined,
          sow_density: c.sowDensityPerTray,
          sow_density_unit: c.sowDensityUnit ?? preferredWeightUnit,
          notes: c.notes,
          create_crop_steps: c.steps.map((s) => ({
            name: s.name,
            description: s.description,
            time_to_start_after_first_step: daysToSeconds(s.startDay),
          })),
          sizes: c.sizes.map((s) => ({
            size: s.size,
            unit: s.unit,
            price: s.price,
          })),
          buffer_percentage: c.bufferPercentage ?? 0,
        })),
      },
      onCompleted: (data) => {
        const numCreated = data?.createCrops?.new_crops?.length ?? 0;
        const numUpdated = data?.createCrops?.crop_updated_count ?? 0;
        enqueueSnackbar(`${numCreated} crops imported. ${numUpdated} crops updated.`, { variant: 'success' });
        setHeaderErrors([]);
        setRowErrors([]);
        setUploadedValues([]);
        setFileSelected(null);
        onSuccess();
      },
      onError: (err) => {
        enqueueSnackbar(err.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'crop_import_error', error: err });
      },
    });
  };

  const hasInputValidationError = uploadedValues.length === 0 || headerErrors.length > 0;
  const isSubmitDisabled = loading || hasInputValidationError;
  // const onEnter =
  //   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  //   (_event: React.KeyboardEvent<HTMLInputElement>) => {
  //     if (!isSubmitDisabled) {
  //       onSubmit();
  //     }
  //   };
  const errorRowCount = new Set(rowErrors.map((r) => r.rowIndex)).size;

  const form = (
    <Box display="flex" flexDirection="column" sx={{ p: { xs: 0, sm: 1 } }} gap={1}>
      <Box
        display="flex"
        sx={{
          gap: { xs: 1, sm: 0 },
          alignItems: { xs: undefined, sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Button
          onClick={() => downloadCropImportTemplate()}
          variant="contained"
          sx={{ textWrap: 'nowrap', mr: { xs: 0, sm: 1 } }}
        >
          Get Template
        </Button>
        <FileUpload
          onFileSelect={(file) => {
            setHeaderErrors([]);
            setRowErrors([]);
            setUploadedValues([]);
            setFileSelected(file);
            Papa.parse(file, {
              header: true,
              error: (err) => {
                enqueueSnackbar(`Something went wrong when parsing the file: ${err.message}`, {
                  variant: 'error',
                  autoHideDuration: 10000,
                });
                clientLogger.error({ title: 'crop_import_error', error: err });
              },
              complete: (results) => {
                const headers = results.meta.fields;
                const headerErrorsIn = validateHeaders(headers ?? []);
                if (headerErrorsIn.length > 0) {
                  setHeaderErrors(headerErrorsIn);
                  return;
                }
                // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                const validatedData = validateCropImportRows(results.data as any);
                setUploadedValues(validatedData.resultValues);
                setRowErrors(validatedData.resultErrors);
              },
            });
          }}
        />
      </Box>
      {fileSelected == null ? (
        <Typography>
          Download the template and fill it out with the crops you want to import. Then select the file, review the
          processed results, and import your crops. Detailed instructions for how to format your data correctly are
          contained in the CSV import template.
        </Typography>
      ) : headerErrors.length > 0 ? (
        <Typography>
          The header row of the CSV file is not correct. Please use the template file to ensure the headers are correct.
          The following errors were found:
          <ul>
            {headerErrors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
          Please fix the errors and try again.
        </Typography>
      ) : (
        <>
          {uploadedValues.length > 0 ? (
            <>
              <Typography>
                {uploadedValues.length} {uploadedValues.length > 1 ? 'rows were' : 'row was'} successfully processed!
                Click "Import" to import these crops.
                {rowErrors.length > 0
                  ? ` ${errorRowCount} ${
                      errorRowCount > 1 ? 'rows were' : 'row was'
                    } not successfully processed. See the errors below.`
                  : ''}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Box
                  display="flex"
                  flexDirection="row"
                  flexWrap="nowrap"
                  alignItems="center"
                  justifyContent="flex-start"
                >
                  <FormControlLabel
                    label="Edit existing crops with the same name"
                    control={
                      <Checkbox
                        disabled={loadingExistingCrops || loading}
                        checked={shouldEditExisting}
                        onChange={(e) => setShouldEditExisting(e.target.checked)}
                        slotProps={{ input: { 'aria-label': 'Edit existing crops with the same name' } }}
                      />
                    }
                  />
                  <Help tooltip="By checking this, Microgreen Manager will check for any crops being uploaded that already exist in your farm based on the crop name (the name must be exactly the same). If this is checked and a crop already exists, then Microgreen Manager will edit that crop instead of creating a new one." />
                </Box>
                {shouldEditExisting && (
                  <WarningBox
                    message={
                      <>
                        ⚠️ Editing existing crops through CSV import is an <b>advanced</b> feature. It is recommended to
                        edit crop parameters individually in the app due to the potential side effects of editing
                        existing crops.
                      </>
                    }
                  />
                )}
              </Box>
              <ParseCropsTable
                crops={uploadedValues}
                existingCropNamesSet={existingCropNamesSet}
                shouldEditExisting={shouldEditExisting}
              />
            </>
          ) : (
            <Typography>
              No rows were successfully processed. Microgreen Manager will stop reading the file once it reads an empty
              row. Make sure your crops are entered before any empty rows.
              {errorRowCount > 0 ? ' Please check the errors below and try again.' : ''}
            </Typography>
          )}
          {rowErrors.length > 0 && (
            <>
              <Typography>
                {errorRowCount > 1 ? `${errorRowCount} rows have errors and were` : 'One row had errors and was'} not
                processed. Review the errors and try again. You can also still import the rows that were processed
                successfully.
              </Typography>
              <ParseErrorsTable parseErrors={rowErrors} />
            </>
          )}
        </>
      )}
    </Box>
  );

  const onCloseInternal = () => {
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onCloseInternal();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Import Crops With CSV</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onCloseInternal} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{form}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: 'column', alignItems: 'center' }}>
        <Button sx={{ width: '100%' }} variant="outlined" onClick={onCloseInternal} disabled={loading}>
          Cancel
        </Button>
        <Button
          sx={{ width: '100%', mt: 1 }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}
